import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { Box, Divider, Link, List, ListItem, ListItemText, Popover, Typography } from '@mui/material';

import moment from 'moment';

import {
  CatalogueSyncFinishedNotification,
  CatalogueSyncStatus,
  LampSimilarityFinishedNotification,
  NotificationEventType,
  NotificationResponse,
  ProjectCalculationFinishedNotification,
  ProjectRenderBakingFinishedNotification,
} from '../../types/notification';

interface NotificationsPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  notifications: NotificationResponse[];
}

function NotificationPrimary({ n, onClosePopover }: { n: NotificationResponse; onClosePopover?: () => void }) {
  const { t } = useTranslation();
  const event = n.event;
  const type = event?.type as NotificationEventType | undefined;

  switch (type) {
    case NotificationEventType.PROJECT_CALCULATION_FINISHED: {
      const evt = event as ProjectCalculationFinishedNotification;
      const status = evt.status;
      const projectLabel = evt.projectName;
      const versionLabel = evt.versionName;
      const truncate = (s: string, n = 42) => (s.length > n ? s.slice(0, n - 1) + '…' : s);

      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('notifications.calculationFinished')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {truncate(projectLabel)} • {truncate(versionLabel)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('notifications.result')}: {status}
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <Link
              component={RouterLink}
              to={`/workspace?projectId=${evt.projectId}&versionId=${evt.versionId}`}
              underline="hover"
              color="primary"
              onClick={() => onClosePopover?.()}
            >
              {t('notifications.openInWorkspace')}
            </Link>
          </Box>
        </Box>
      );
    }
    case NotificationEventType.PROJECT_RENDER_BAKING_FINISHED: {
      const evt = event as ProjectRenderBakingFinishedNotification;
      const status = evt.status;
      const projectLabel = evt.projectName;
      const versionLabel = evt.versionName;
      const truncate = (s: string, n = 42) => (s.length > n ? s.slice(0, n - 1) + '…' : s);

      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('notifications.renderBakingFinished')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {truncate(projectLabel)} • {truncate(versionLabel)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('notifications.result')}: {status}
          </Typography>

          <Box sx={{ mt: 0.5 }}>
            <Link
              component={RouterLink}
              to={`/workspace?projectId=${evt.projectId}&versionId=${evt.versionId}`}
              underline="hover"
              color="primary"
              onClick={() => onClosePopover?.()}
            >
              {t('notifications.openInWorkspace')}
            </Link>
          </Box>
        </Box>
      );
    }
    case NotificationEventType.LAMP_SIMILARITY_FINISHED: {
      const evt = event as LampSimilarityFinishedNotification;
      const status = evt.status;
      const projectLabel = evt.projectName;
      const versionLabel = evt.versionName;
      const truncate = (s: string, n = 42) => (s.length > n ? s.slice(0, n - 1) + '…' : s);

      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('notifications.lampSimilarityFinished')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {truncate(projectLabel)} • {truncate(versionLabel)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('notifications.result')}: {status}
          </Typography>

          <Box sx={{ mt: 0.5 }}>
            <Link
              component={RouterLink}
              to={`/workspace?projectId=${evt.projectId}&versionId=${evt.versionId}`}
              underline="hover"
              color="primary"
              onClick={() => onClosePopover?.()}
            >
              {t('notifications.openInWorkspace')}
            </Link>
          </Box>
        </Box>
      );
    }
    case NotificationEventType.CATALOGUE_SYNC_FINISHED: {
      const evt = event as CatalogueSyncFinishedNotification;
      const isSuccess = evt.status === CatalogueSyncStatus.SUCCESS;
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('notifications.catalogueSyncFinished')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('notifications.result')}: {evt.status}
          </Typography>
          {isSuccess && evt.commitSha && (
            <Typography variant="body2" color="text.secondary">
              {t('notifications.latestSha')}:{' '}
              <Box component="span" sx={{ fontFamily: 'monospace' }}>
                {evt.commitSha.slice(0, 7)}
              </Box>
            </Typography>
          )}
          {!isSuccess && evt.errorMessage && (
            <Typography variant="body2" color="text.secondary">
              {evt.errorMessage}
            </Typography>
          )}
        </Box>
      );
    }
    case NotificationEventType.GENERIC:
    default:
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('notifications.genericNotification')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('notifications.detailsSoon')}
          </Typography>
        </Box>
      );
  }
}

export const NotificationsPopover: React.FC<NotificationsPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  notifications,
}) => {
  const { t } = useTranslation();
  const hasNotifications = notifications && notifications.length > 0;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: {
          mt: 1,
          width: 360,
          maxHeight: 420,
          bgcolor: '#111111',
          color: 'white',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          backdropFilter: 'none',
        },
      }}
    >
      <Box sx={{ p: 1.5 }}>
        <Typography variant="subtitle1">{t('notifications.title')}</Typography>
      </Box>
      <Divider />
      {!hasNotifications ? (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('notifications.allCaughtUp')}
          </Typography>
        </Box>
      ) : (
        <List dense sx={{ py: 0, overflowY: 'auto', maxHeight: 360 }}>
          {notifications.map((n, idx) => (
            <React.Fragment key={n.id}>
              <ListItem alignItems="flex-start" sx={{ py: 1.25 }}>
                <ListItemText
                  primary={<NotificationPrimary n={n} onClosePopover={onClose} />}
                  secondary={moment(n.createdAt).fromNow()}
                  secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                />
              </ListItem>
              {idx < notifications.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Popover>
  );
};
